import { useState } from "react";

import { TextArea } from "@king-ict/eupisi-ui/components";
import { Button } from "react-bootstrap";
import { useTranslation } from "react-i18next";

import styles from "./NoteContainer.module.scss";

interface IProps {
  title?: string;
  date?: string;
  note?: string;
  showButtons?: {
    edit?: boolean;
    delete?: boolean;
    save?: boolean;
  };
  onEdit?: () => void;
  onDelete?: () => void;
  onSave?: (note: string) => void;
  onCancel?: () => void;
  isAdding?: boolean;
}

export const NoteContainer = ({
  title = "",
  date = "",
  note = "",
  showButtons,
  onEdit,
  onDelete,
  onSave,
  onCancel,
  isAdding,
}: IProps) => {
  const { t } = useTranslation();
  const [isEditing, setIsEditing] = useState(isAdding);
  const [editedNote, setEditedNote] = useState(note);

  const handleEdit = () => {
    setIsEditing(true);
    setEditedNote(note);
    onEdit?.();
  };

  const handleSave = () => {
    setIsEditing(false);
    onSave?.(editedNote);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditedNote(note);
    onCancel?.();
  };

  const getNoteTitle = () => {
    if (isAdding) {
      return t("schoolAttendance.newNote");
    }

    return title;
  };

  const generateNoteContent = () => {
    if (!isEditing) {
      return note;
    }

    return (
      <TextArea
        value={editedNote}
        onChange={(e) => setEditedNote(e.target.value)}
        rows={4}
        placeholder={t("schoolAttendance.enterNote")}
        ariaLabel={t("schoolAttendance.enterNote")}
      />
    );
  };

  const generateNoteActions = () => {
    if (isEditing) {
      return (
        <>
          <Button variant="primary" size="sm" onClick={handleSave}>
            {t("common.save")}
          </Button>
          <Button variant="outline-secondary" size="sm" onClick={handleCancel}>
            {t("common.cancel")}
          </Button>
        </>
      );
    }

    return (
      <>
        {!!showButtons?.edit && (
          <Button variant="outline-primary" size="sm" onClick={handleEdit}>
            {t("common.edit")}
          </Button>
        )}
        {!!showButtons?.delete && (
          <Button variant="outline-danger" size="sm" onClick={onDelete}>
            {t("common.delete")}
          </Button>
        )}
        {!!showButtons?.save && (
          <Button variant="primary" size="sm" onClick={handleSave}>
            {t("common.save")}
          </Button>
        )}
      </>
    );
  };

  return (
    <div className={styles.noteContainer}>
      <div className={styles.noteHeader}>
        <div className={styles.noteDate}>{date}</div>
        <div className={styles.noteTitle}>{getNoteTitle()}</div>
      </div>
      <div className={styles.noteContent}>{generateNoteContent()} </div>
      <div className={styles.noteActions}>{generateNoteActions()}</div>
    </div>
  );
};
